import { makeStyles } from '@material-ui/core/styles'

export { priorityMeta, scheduleMeta, queueStatusMeta, dyeMeta, packetMeta } from '../v3/v3Styles'

export const tokens = {
  bg: { page: '#fafbfc', card: '#fff', panel: '#f7f9f8', hover: '#f5f7fa', selected: '#e8f0fe' },
  border: { light: '#e8e8e8', medium: '#d0d0d0' },
  text: { primary: '#1a1a1a', secondary: '#666', muted: '#999', hint: '#aaa' },
  accent: { brown: '#8d6e63', brownLight: '#efebe9', blue: '#1565c0' }
}

export const useV4Styles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    maxHeight: '100vh',
    minHeight: 0,
    overflow: 'hidden',
    background: tokens.bg.page
  },
  statsBar: {
    display: 'flex',
    gap: 8,
    padding: '10px 16px',
    background: tokens.bg.card,
    borderBottom: `1px solid ${tokens.border.light}`,
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  filterBar: {
    padding: '8px 16px',
    background: tokens.bg.card,
    borderBottom: `1px solid ${tokens.border.light}`,
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  contentShell: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden'
  },
  workspacePane: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden'
  },
  mainContent: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden'
  },
  queuePanel: {
    width: 340,
    borderRight: `1px solid ${tokens.border.light}`,
    minHeight: 0,
    overflowY: 'auto',
    background: tokens.bg.card
  },
  detailPanel: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(2.5, 3),
    background: tokens.bg.page
  },
  detailStateCard: {
    minHeight: 260,
    padding: theme.spacing(3),
    background: tokens.bg.card,
    border: `1px solid ${tokens.border.light}`,
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)'
  },
  detailEmptyState: {
    minHeight: 320,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(4),
    background: tokens.bg.card,
    border: '1px dashed #d7dee2',
    borderRadius: 12
  },
  detailSkeletonLine: {
    height: 12,
    borderRadius: 999,
    marginBottom: 10,
    background: 'linear-gradient(90deg, #eef2f4 0%, #f8fafb 50%, #eef2f4 100%)'
  },
  detailSkeletonFacts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))',
    gap: 14,
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(120px, 1fr))'
    }
  },
  detailSkeletonFact: {
    padding: theme.spacing(1.5),
    border: '1px solid #edf0f2',
    borderRadius: 8
  },
  activityPanel: {
    width: 360,
    flexShrink: 0,
    borderLeft: `1px solid ${tokens.border.light}`,
    minHeight: 0,
    overflowY: 'auto',
    background: tokens.bg.panel,
    [theme.breakpoints.down('md')]: {
      width: 310
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  activityPanelHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 14px',
    background: tokens.bg.card,
    borderBottom: `1px solid ${tokens.border.light}`
  },
  activityPanelControls: {
    padding: '10px 12px 6px 12px',
    background: tokens.bg.card
  },
  activityPanelHint: {
    display: 'block',
    padding: '0 12px 10px 12px',
    color: '#78909c',
    background: tokens.bg.card,
    borderBottom: '1px solid #eef1f2'
  },
  activityItem: {
    padding: '12px 14px',
    borderBottom: '1px solid #e9edef',
    background: tokens.bg.card,
    cursor: 'pointer',
    '&:hover': {
      background: tokens.bg.hover
    }
  },
  activityItemCompact: {
    padding: '8px 0',
    borderBottom: '1px solid #f1f3f4',
    cursor: 'pointer',
    '&:hover': {
      background: '#fafafa'
    }
  },
  activityIcon: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  activityTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: tokens.text.primary,
    lineHeight: 1.25
  },
  activitySite: {
    display: 'block',
    color: '#607d8b',
    lineHeight: 1.2
  },
  activityBody: {
    color: '#455a64',
    fontSize: 12,
    marginTop: 6,
    lineHeight: 1.35
  },
  activityMeta: {
    display: 'block',
    color: '#90a4ae',
    marginTop: 6,
    fontSize: 10
  },
  activityPhotos: {
    display: 'flex',
    gap: 6,
    marginTop: 8,
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  activityPhoto: {
    width: 54,
    maxHeight: 48,
    objectFit: 'cover',
    borderRadius: 4,
    border: '1px solid #dde5e8',
    background: '#eceff1'
  },
  activityPhotoMore: {
    minWidth: 34,
    height: 28,
    borderRadius: 14,
    background: '#eceff1',
    color: '#546e7a',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 700
  },
  footerBar: {
    position: 'sticky',
    bottom: 0,
    zIndex: 4,
    borderTop: `1px solid ${tokens.border.light}`,
    background: tokens.bg.card,
    boxShadow: '0 -4px 14px rgba(15, 23, 42, 0.06)'
  },

  // Queue card styles
  queueCard: {
    padding: '12px 14px',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    borderLeft: '3px solid transparent',
    transition: 'background 0.12s ease, box-shadow 0.12s ease',
    '&:hover': {
      background: tokens.bg.hover
    }
  },
  queueCardSelected: {
    background: `${tokens.bg.selected} !important`,
    borderLeftColor: `${tokens.accent.blue} !important`,
    boxShadow: '0 1px 4px rgba(21,101,192,0.10)'
  },
  queueCardName: {
    fontWeight: 600,
    color: tokens.text.primary,
    fontSize: 13
  },
  queueCardAddress: {
    fontSize: 11,
    color: tokens.text.muted,
    marginTop: 1
  },
  queueCardMeta: {
    display: 'flex',
    gap: 6,
    marginTop: 4,
    alignItems: 'center'
  },
  queueCardAreas: {
    display: 'flex',
    gap: 8,
    marginTop: 4,
    alignItems: 'center'
  },
  queueCardActivity: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 6
  },
  queueActivityBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    padding: '2px 6px',
    fontSize: 9,
    fontWeight: 700,
    lineHeight: 1.2
  },

  // Detail panel styles
  detailHeader: {
    marginBottom: theme.spacing(2)
  },
  quickFactsCard: {
    display: 'flex',
    gap: 0,
    padding: 0,
    background: tokens.bg.card,
    borderRadius: 6,
    border: `1px solid ${tokens.border.light}`,
    marginBottom: 16,
    flexWrap: 'wrap',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    alignItems: 'stretch'
  },
  quickFactItem: {
    flex: '1 1 0',
    minWidth: 120,
    padding: '12px 16px',
    borderLeft: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    '&:first-child': {
      borderLeft: 'none'
    }
  },
  quickFactTicketValue: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 2,
    minHeight: 22,
    flexWrap: 'wrap'
  },
  quickFactTicketLink: {
    fontSize: 13,
    fontWeight: 700,
    lineHeight: '18px',
    display: 'inline-block',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    },
    '&:focus': {
      outline: 'none',
      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.18)',
      borderRadius: 4
    }
  },
  quickFactTicketDate: {
    color: '#6f8791',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: '18px'
  },
  actionBar: {
    display: 'flex',
    gap: 8,
    marginBottom: theme.spacing(2)
  },
  collapsibleSection: {
    background: tokens.bg.card,
    borderRadius: 8,
    border: `1px solid ${tokens.border.light}`,
    marginBottom: theme.spacing(1.25)
  },
  sectionGroup: {
    marginTop: 20
  },
  sectionHeader: {
    padding: '10px 14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    userSelect: 'none'
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: tokens.text.primary
  },
  sectionBody: {
    padding: '0 14px 12px 14px'
  },

  // Queue list header
  queueListHeader: {
    padding: '8px 12px',
    fontSize: 11,
    color: tokens.text.secondary,
    letterSpacing: 0.5,
    borderBottom: `1px solid ${tokens.border.light}`,
    paddingBottom: 8
  },

  // Inline edit field
  inlineEditDisplay: {
    cursor: 'pointer',
    borderRadius: 3,
    padding: '1px 0',
    borderBottom: '1px dashed #ccc',
    '&:hover': {
      borderBottom: '1px solid #999'
    }
  },
  inlineEditActive: {
    width: '100%',
    border: 'none',
    borderBottom: `2px solid ${tokens.accent.blue}`,
    outline: 'none',
    fontSize: 12,
    padding: '2px 0',
    background: 'transparent',
    fontFamily: 'inherit'
  },

  // Activity badge as text label
  activityBadgeLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.3
  }
}))
